import {LLMToolCallBlock, LLMToolResultBlock} from 'llm/deep-research/DeepResearchDAO';
import {ProgressToolCall} from 'llm/deep-research/tool-details/types';

import {
  DeepResearchToolType,
  ToolCallStatus,
  SearchToolCall,
  GetWarehouseOverviewToolCall,
  QueryWarehouseToolCall,
  NotebookQueryToolCall,
  AttachmentToolCall,
  BaseProgressToolCall,
} from './types';

// Utility function to extract text content from tool result
function extractResultText(toolResult: LLMToolResultBlock): string {
  return toolResult.value.content
    .filter((content) => content.type === 'text')
    .map((content) => {
      const textBlock = content;
      if (textBlock.value.text) {
        return textBlock.value.text;
      }
      return '';
    })
    .join(' ')
    .trim();
}

// Base function to create common tool call properties
function createBaseToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): BaseProgressToolCall {
  const resultText = toolResultBlock ? extractResultText(toolResultBlock) : undefined;

  return {
    tool: toolCallBlock.value.name,
    toolId: toolCallBlock.value.id,
    createdAt,
    status: toolResultBlock ? ToolCallStatus.COMPLETED : ToolCallStatus.PENDING,
    responseText: resultText,
  };
}

// Individual tool call parsers
function parseSearchToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): SearchToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.SearchTool,
    query: (toolCallBlock.value.args.query as string) || '',
    objectTypes: (toolCallBlock.value.args.objectTypes as Array<string>) || [],
  };
}

function parseGetWarehouseOverviewToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): GetWarehouseOverviewToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.GetWarehouseOverviewTool,
  };
}

function parseQueryWarehouseToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): QueryWarehouseToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.QueryWarehouseTool,
    query: (toolCallBlock.value.args.query as string) || '',
  };
}

function parseNotebookQueryToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): NotebookQueryToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.NotebookQueryTool,
    notebookId: (toolCallBlock.value.args.notebookId as string) || '',
  };
}

function parseAttachmentToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): AttachmentToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.AttachmentTool,
    attachmentId: (toolCallBlock.value.args.attachmentId as string) || '',
  };
}

function parseGenericToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): BaseProgressToolCall {
  return createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);
}

// Tool parser registry
const toolParsers: Record<
  DeepResearchToolType,
  (
    toolCallBlock: LLMToolCallBlock,
    toolResultBlock: LLMToolResultBlock | null,
    createdAt: Date
  ) => ProgressToolCall
> = {
  [DeepResearchToolType.SearchTool]: parseSearchToolCall,
  [DeepResearchToolType.GetWarehouseOverviewTool]: parseGetWarehouseOverviewToolCall,
  [DeepResearchToolType.QueryWarehouseTool]: parseQueryWarehouseToolCall,
  [DeepResearchToolType.NotebookQueryTool]: parseNotebookQueryToolCall,
  [DeepResearchToolType.AttachmentTool]: parseAttachmentToolCall,
  [DeepResearchToolType.ResearchSubAgent]: parseGenericToolCall,
  [DeepResearchToolType.Think]: parseGenericToolCall,
  [DeepResearchToolType.CompleteAnswer]: parseGenericToolCall,
};

/**
 * Main function to convert a tool call and optional result into a ProgressToolCall
 */
export function getToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): ProgressToolCall | null {
  const toolName = toolCallBlock.value.name;
  const parser = toolParsers[toolName];

  if (!parser) {
    return null;
  }

  return parser(toolCallBlock, toolResultBlock, createdAt);
}
